import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Admin status — server-validated single source of truth.
 *
 * Sibling of `useApprovalStatus`. Pre-2026-05-13 the web read
 * `user.app_metadata.role === 'admin'` from the JWT inside `useAuth`'s
 * `extractRole(user)` helper. That snapshot only refreshes on re-login, so
 * admin promote/demote changes didn't propagate until the user signed out and
 * back in (same staleness bug `auth-lifecycle-hardening` fixed for the
 * `approved` claim). This hook delegates to `GET /api/users/me/admin-status`
 * which reads JWT `app_metadata.role` server-side (reverted from a brief
 * DB-backed implementation on 2026-05-14 after a field-collision incident —
 * `user_profiles.role` is the user-writable WORK role from signup, NOT the
 * admin claim; see `auth.middleware.ts:requireAdmin` JSDoc for the full
 * story). The 60s staleTime is acceptable for the rare admin promote/demote
 * cadence — a future `user_profiles.is_admin boolean` with dual-write
 * semantics is tracked as backlog `admin-status-canonical-column`.
 *
 * IMPORTANT: this hook takes `userId` as an argument rather than reading it
 * via `useAuth()`. Reason: `use-auth.tsx` calls `useAdminStatus(user?.id)`
 * inside `AuthProvider` — a `useAuth()` call here would create a circular
 * import (use-auth ↔ use-admin-status). The userId-as-arg form lets
 * `AuthProvider` pass its own `user.id`; standalone callers pass `undefined`
 * and the hook stays disabled until a userId arrives.
 *
 * signup-billing-mvp Phase 5 Session 4 (2026-05-13).
 */
interface AdminStatusResponse {
  is_admin: boolean;
}

interface AdminStatusEnvelope {
  data: AdminStatusResponse;
}

export function useAdminStatus(userId: string | undefined) {
  const query = useQuery<AdminStatusResponse>({
    queryKey: ['users', 'me', 'admin-status', userId ?? 'anon'],
    queryFn: async () => {
      // The API wraps every response in `{ data: ... }` (sendSuccess envelope);
      // request<T> doesn't unwrap, so we have to here. Mirrors
      // `useApprovalStatus`'s envelope-unwrap convention.
      const envelope = await api.get<AdminStatusEnvelope>(
        '/api/users/me/admin-status'
      );
      return envelope.data;
    },
    enabled: !!userId,
    staleTime: 60_000,
    // refetchOnWindowFocus removed 2026-05-27 — handled centrally by
    // <FocusResumeCoordinator> (Tier-1 immediate invalidation on tab resume).
  });

  return {
    isAdmin: query.data?.is_admin ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
